import { useEffect } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i;

function getErrorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) return error.statusText || `Request failed (${error.status})`;
  if (error instanceof Error) return error.message;
  return 'The page could not be loaded.';
}

export function RouteErrorPage() {
  const error = useRouteError();
  const message = getErrorMessage(error);
  const isChunkError = CHUNK_ERROR_PATTERN.test(message);

  useEffect(() => {
    if (!isChunkError) return;

    const key = `scimodeling-chunk-reload:${window.location.pathname}`;
    const lastReload = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - lastReload > 30_000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
  }, [isChunkError]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-md text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-semibold">
          {isChunkError ? 'The app was updated' : 'This page could not be loaded'}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isChunkError ? 'Reload to use the latest version.' : message}
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Button onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            Return Home
          </Button>
        </div>
      </div>
    </main>
  );
}
